import { currencyFormatter, toDecimal } from "shared";
import {
  BanknotesIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  CalendarDaysIcon,
  DocumentCurrencyDollarIcon,
  ShieldCheckIcon,
} from "@heroicons/react/24/outline";
import { Tooltip } from "@mui/material";
import { useMemo } from "react";

import { classNames } from "../../../common/lib/classNames";
import { useGetSelectedOrganisation } from "../../hooks/useGetSelectedOrganisation";
import { useContact } from "../../hooks/useContact";

import { LoadingOverlay } from "./LoadingOverlay";

export function ContactStatsDisplay() {
  const { data: contact, isLoading, isFetching } = useContact();

  const currentOrg = useGetSelectedOrganisation();
  const formatter = currencyFormatter(currentOrg?.baseCurrency || `USD`);
  const possibleCurrencies = contact?.uniqueCurrencies || [];

  const currencyBreakdown = useMemo(() => {
    if (!contact?.balances) {
      return null;
    }

    const items: {
      code: string;
      outstanding: string;
      overdue: string;
      totalRevenue: string;
    }[] = [];

    possibleCurrencies.forEach(cur => {
      const balances = contact.balances[cur];
      if (!balances) return;
      const formatter = currencyFormatter(cur, currentOrg?.baseCurrency || `USD`);

      items.push({
        code: cur,
        outstanding: formatter(toDecimal(balances.outstandingCents)),
        overdue: formatter(toDecimal(balances.overdueCents)),
        totalRevenue: formatter(toDecimal(balances.totalRevenueCents)),
      });
    });

    return items;
  }, [contact, currentOrg]);

  // Show breakdowns if any currency differs from base currency
  const showBreakdown = useMemo(() => {
    if (!currencyBreakdown || !currencyBreakdown.length) return false;

    return currencyBreakdown.some(item => item.code !== currentOrg?.baseCurrency);
  }, [currencyBreakdown, currentOrg?.baseCurrency]);

  function getTextColor(value: number, range: {
    high: number;
    medium?: number;
    low: number;
  }, lowIsGreen?: boolean) {
    if (value >= range.high) {
      return {
        text: `text-rose-700`,
        iconBg: `bg-rose-50`,
        iconText: `text-rose-600`,
      };
    }

    if (range.medium !== undefined && value >= range.medium) {
      return {
        text: `text-amber-700`,
        iconBg: `bg-amber-50`,
        iconText: `text-amber-600`,
      };
    }

    if (lowIsGreen) {
      return {
        text: `text-green-700`,
        iconBg: `bg-green-50`,
        iconText: `text-green-600`,
      };
    }

    return {
      text: `text-gray-700`,
      iconBg: `bg-gray-50`,
      iconText: `text-gray-600`,
    };
  }

  if (!contact) {
    return null;
  }

  const outstanding = toDecimal(contact.outstandingBalanceHome || 0);
  const overdue = toDecimal(contact.overdueBalanceHome || 0);
  const totalRevenueHome = toDecimal(contact.totalRevenueHome || 0);
  const totalRevenueHomeLast12Months = toDecimal(contact.totalRevenueHomeLast12Months || 0);
  const avgDaysToPay = contact.avgDaysToPay || 0;
  const oldestDaysOverdue = contact.oldestDaysOverdue || 0;
  const unappliedCreditsHome = toDecimal(contact.unappliedCreditsTotalHome || 0);
  const creditScore = contact.creditScore ?? null;
  const weightedAvgDaysLate = contact.weightedAvgDaysLate ?? null;

  const overdueColors = getTextColor(overdue, {
    high: 1,
    low: 0,
  });

  const avgDaysToPayColors = getTextColor(avgDaysToPay, {
    high: 60,
    medium: 40,
    low: 10,
  }, true);

  const oldestDaysOverdueColors = getTextColor(oldestDaysOverdue, {
    high: 90,
    medium: 60,
    low: 30,
  }, true);

  // Score runs the other way: low is bad, so invert it for the shared colour helper
  const creditScoreColors = creditScore === null
    ? getTextColor(0, { high: 1, low: 0 })
    : getTextColor(100 - creditScore, {
      high: 60,
      medium: 40,
      low: 0,
    }, true);

  const creditScoreTooltip = creditScore === null
    ? `No payment history and nothing outstanding yet`
    : [
      `0-100 payment behaviour score`,
      weightedAvgDaysLate === null
        ? `No payments recorded`
        : `Pays ${Math.abs(Math.round(weightedAvgDaysLate))} days ${weightedAvgDaysLate > 0 ? `late` : `early`} on average (recent and larger payments count more)`,
      `${formatter(overdue)} of ${formatter(outstanding)} outstanding is overdue, oldest ${oldestDaysOverdue} days`,
    ].join(`. `);

  return (
    <LoadingOverlay
      loading={ isLoading || isFetching }
    >
      <div
        id={ `escalation-subheader-stats` }
        className={ `mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3 lg:grid-cols-4 xl:grid-cols-7` }
      >
        <Tooltip title={ `Converted to ${currentOrg?.baseCurrency || `No Organisation`}` }>
          <div className={ `group relative overflow-hidden rounded-lg bg-gray-50 ring-1 ring-gray-200 p-3 hover:bg-white hover:shadow-xs transition` }>
            <div className={ `flex items-start gap-3` }>
              <div className={ `rounded-md bg-indigo-50 p-2` }>
                <BanknotesIcon className={ `h-5 w-5 text-indigo-600` } />
              </div>
              <div className={ `min-w-0` }>
                <div className={ `text-[11px] uppercase tracking-wide text-gray-500` }>{ `Outstanding` }</div>
                <div className={ `text-lg font-semibold text-gray-900 truncate` }>{ formatter(outstanding) }</div>
                {
                  showBreakdown ? (
                    <div className={ `mt-1 ml-2 pl-3 border-l border-gray-200 space-y-1` }>
                      {
                        currencyBreakdown.map(item => (
                          <div
                            key={ `out-${item.code}` }
                            className={ `text-[11px] text-gray-600 leading-tight` }
                          >
                            { item.outstanding }
                          </div>
                        ))
                      }
                    </div>
                  ) : null
                }
              </div>
            </div>
          </div>
        </Tooltip>

        <Tooltip title={ `Converted to ${currentOrg?.baseCurrency || `No Organisation`}` }>
          <div className={ `group relative overflow-hidden rounded-lg bg-gray-50 ring-1 ring-gray-200 p-3 hover:bg-white hover:shadow-xs transition` }>
            <div className={ `flex items-start gap-3` }>
              <div className={ classNames(`rounded-md p-2`, overdueColors.iconBg) }>
                <ExclamationTriangleIcon className={ classNames(`h-5 w-5`, overdueColors.iconText) } />
              </div>
              <div className={ `min-w-0` }>
                <div className={ `text-[11px] uppercase tracking-wide text-gray-500` }>{ `Overdue` }</div>
                <div className={ classNames(`text-lg font-semibold truncate`, overdueColors.text) }>{ formatter(overdue) }</div>
                {
                  showBreakdown ? (
                    <div className={ `mt-1 ml-2 pl-3 border-l border-gray-200 space-y-1` }>
                      {
                        currencyBreakdown.map(item => (
                          <div
                            key={ `overdue-${item.code}` }
                            className={ `text-[11px] leading-tight ${overdueColors.iconText}` }
                          >
                            { item.overdue }
                          </div>
                        ))
                      }
                    </div>
                  ) : null
                }
              </div>
            </div>
          </div>
        </Tooltip>

        <Tooltip title={ `Converted to ${currentOrg?.baseCurrency || `No Organisation`}` }>
          <div className={ `group relative overflow-hidden rounded-lg bg-gray-50 ring-1 ring-gray-200 p-3 hover:bg-white hover:shadow-xs transition` }>
            <div className={ `flex items-start gap-3` }>
              <div className={ `rounded-md bg-emerald-50 p-2` }>
                <BanknotesIcon className={ `h-5 w-5 text-emerald-600` } />
              </div>
              <div className={ `min-w-0` }>
                <div className={ `text-[11px] uppercase tracking-wide text-gray-500` }>{ `Total Revenue` }</div>
                <div className={ `text-lg font-semibold text-gray-900 truncate` }>{ formatter(totalRevenueHome) }</div>
                <div className={ `text-[11px] text-gray-600 leading-tight truncate` }>{ `Last 12 months: ${formatter(totalRevenueHomeLast12Months)}` }</div>
                {
                  showBreakdown ? (
                    <div className={ `mt-1 ml-2 pl-3 border-l border-gray-200 space-y-1` }>
                      {
                        currencyBreakdown.map(item => (
                          <div
                            key={ `tr-${item.code}` }
                            className={ `text-[11px] text-gray-600 leading-tight` }
                          >
                            { item.totalRevenue }
                          </div>
                        ))
                      }
                    </div>
                  ) : null
                }
              </div>
            </div>
          </div>
        </Tooltip>

        <Tooltip title={ `Converted to ${currentOrg?.baseCurrency || `No Organisation`}` }>
          <div className={ `group relative overflow-hidden rounded-lg bg-gray-50 ring-1 ring-gray-200 p-3 hover:bg-white hover:shadow-xs transition` }>
            <div className={ `flex items-start gap-3` }>
              <div className={ `rounded-md bg-violet-50 p-2` }>
                <DocumentCurrencyDollarIcon className={ `h-5 w-5 text-violet-600` } />
              </div>
              <div className={ `min-w-0` }>
                <div className={ `text-[11px] uppercase tracking-wide text-gray-500` }>{ `Current Credits` }</div>
                <div className={ `text-lg font-semibold text-gray-900 truncate` }>{ formatter(unappliedCreditsHome) }</div>
              </div>
            </div>
          </div>
        </Tooltip>

        <div className={ `group relative overflow-hidden rounded-lg bg-gray-50 ring-1 ring-gray-200 p-3 hover:bg-white hover:shadow-xs transition` }>
          <div className={ `flex items-start gap-3` }>
            <div className={ `rounded-md bg-amber-50 p-2` }>
              <ClockIcon className={ `h-5 w-5 text-amber-600` } />
            </div>
            <div className={ `min-w-0` }>
              <div className={ `text-[11px] uppercase tracking-wide text-gray-500` }>{ `Avg Days To Pay` }</div>
              <div className={ classNames(`text-lg font-semibold truncate`, avgDaysToPayColors.text) }>{ avgDaysToPay }</div>
            </div>
          </div>
        </div>

        <div className={ `group relative overflow-hidden rounded-lg bg-gray-50 ring-1 ring-gray-200 p-3 hover:bg-white hover:shadow-xs transition` }>
          <div className={ `flex items-start gap-3` }>
            <div className={ `rounded-md bg-sky-50 p-2` }>
              <CalendarDaysIcon className={ `h-5 w-5 text-sky-600` } />
            </div>
            <div className={ `min-w-0` }>
              <div className={ `text-[11px] uppercase tracking-wide text-gray-500` }>{ `Oldest Due Invoice` }</div>
              <div
                className={ classNames(`text-lg truncate`, oldestDaysOverdueColors.text) }
              >
                <span
                  className={ `font-semibold` }
                >
                  { oldestDaysOverdue }
                </span>
                { ` days` }
              </div>
            </div>
          </div>
        </div>

        <Tooltip title={ creditScoreTooltip }>
          <div className={ `group relative overflow-hidden rounded-lg bg-gray-50 ring-1 ring-gray-200 p-3 hover:bg-white hover:shadow-xs transition` }>
            <div className={ `flex items-start gap-3` }>
              <div className={ classNames(`rounded-md p-2`, creditScoreColors.iconBg) }>
                <ShieldCheckIcon className={ classNames(`h-5 w-5`, creditScoreColors.iconText) } />
              </div>
              <div className={ `min-w-0` }>
                <div className={ `text-[11px] uppercase tracking-wide text-gray-500` }>{ `Credit Score` }</div>
                <div className={ classNames(`text-lg font-semibold truncate`, creditScoreColors.text) }>
                  { creditScore === null ? `No data` : creditScore }
                </div>
                { contact.creditScoreBand ? (
                  <div className={ classNames(`text-[11px] leading-tight truncate`, creditScoreColors.iconText) }>{ contact.creditScoreBand }</div>
                ) : null }
              </div>
            </div>
          </div>
        </Tooltip>
      </div>
    </LoadingOverlay>
  );
}
